import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { apiService } from '../services/api';
import { WritingStyle } from '../types';

export interface StyleOption {
  id?: string;
  value: string;
  label: string;
  description?: string;
  isDefault?: boolean;
}

interface StylesContextType {
  styles: StyleOption[];
  addStyle: (style: Omit<StyleOption, 'id'>) => Promise<void>;
  editStyle: (id: string, newStyle: Omit<StyleOption, 'id'>) => Promise<void>;
  removeStyle: (id: string) => Promise<void>;
  loading: boolean;
  error: string | null;
}

const defaultStyles: StyleOption[] = [
  { 
    value: 'general', 
    label: 'General',
    description: 'Use clear, professional language suitable for general audiences. Correct spelling and grammar while maintaining the original tone.',
    isDefault: true
  },
  { 
    value: 'tweet', 
    label: 'Tweet (Casual, Concise)',
    description: 'Optimize for social media with concise, engaging language. Keep it under character limits while maintaining clarity.',
    isDefault: true
  },
  { 
    value: 'email', 
    label: 'Email (Professional)',
    description: 'Professional business email format with clear subject lines and appropriate formality level.',
    isDefault: true
  },
  { 
    value: 'chat', 
    label: 'Chat (Conversational)',
    description: 'Casual, friendly tone suitable for instant messaging and informal communication.',
    isDefault: true
  },
  { 
    value: 'academic', 
    label: 'Academic (Formal)',
    description: 'Formal academic language with precise terminology, objective tone, and proper citations where needed.',
    isDefault: true
  },
  { 
    value: 'business', 
    label: 'Business (Clear, Professional)',
    description: 'Professional business language that is clear, direct, and results-oriented while maintaining formality.',
    isDefault: true
  }
];

const StylesContext = createContext<StylesContextType | undefined>(undefined);

export function StylesProvider({ children }: { children: ReactNode }) {
  const [styles, setStyles] = useState<StyleOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load styles from backend on mount
  useEffect(() => {
    loadStyles();
  }, []);

  const loadStyles = async () => {
    try {
      setLoading(true);
      setError(null);
      const writingStyles = await apiService.getWritingStyles();
      // Convert WritingStyle to StyleOption format
      const styleOptions: StyleOption[] = writingStyles.map((ws: WritingStyle) => ({
        id: ws.id,
        value: ws.value,
        label: ws.label,
        description: ws.description,
        isDefault: ws.isDefault
      }));
      setStyles(styleOptions);
    } catch (err) {
      console.error('Failed to load writing styles:', err);
      setError('Failed to load writing styles');
      // Fallback to default styles if API fails
      setStyles(defaultStyles);
    } finally {
      setLoading(false);
    }
  };

  const addStyle = async (style: Omit<StyleOption, 'id'>) => {
    try {
      setError(null);
      const newStyle = await apiService.createWritingStyle({
        value: style.value,
        label: style.label,
        description: style.description
      });
      
      const styleOption: StyleOption = {
        id: newStyle.id,
        value: newStyle.value,
        label: newStyle.label,
        description: newStyle.description,
        isDefault: newStyle.isDefault
      };
      
      setStyles((prev) => [...prev, styleOption]);
    } catch (err) {
      console.error('Failed to create writing style:', err);
      setError(err instanceof Error ? err.message : 'Failed to create writing style');
      throw err;
    }
  };

  const editStyle = async (id: string, newStyle: Omit<StyleOption, 'id'>) => {
    try {
      setError(null);
      const updatedStyle = await apiService.updateWritingStyle(id, {
        value: newStyle.value,
        label: newStyle.label,
        description: newStyle.description
      });
      
      const styleOption: StyleOption = {
        id: updatedStyle.id,
        value: updatedStyle.value,
        label: updatedStyle.label,
        description: updatedStyle.description,
        isDefault: updatedStyle.isDefault
      };
      
      setStyles((prev) => prev.map((s) => (s.id === id ? styleOption : s)));
    } catch (err) {
      console.error('Failed to update writing style:', err);
      setError(err instanceof Error ? err.message : 'Failed to update writing style');
      throw err;
    }
  };

  const removeStyle = async (id: string) => {
    try {
      setError(null);
      await apiService.deleteWritingStyle(id);
      setStyles((prev) => prev.filter((s) => s.id !== id));
    } catch (err) {
      console.error('Failed to delete writing style:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete writing style');
      throw err;
    }
  };

  return (
    <StylesContext.Provider value={{ styles, addStyle, editStyle, removeStyle, loading, error }}>
      {children}
    </StylesContext.Provider>
  );
}

export function useStyles() {
  const context = useContext(StylesContext);
  if (!context) {
    throw new Error('useStyles must be used within a StylesProvider');
  }
  return context;
}

